import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, Printer, Download, QrCode } from "lucide-react";
import { toast } from "sonner";
import QRCode from "qrcode";
import html2canvas from "html2canvas";
import { api } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const VLOGO =
  "data:image/svg+xml;base64," +
  btoa(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 80'><path d='M8 12 C20 55 40 70 50 70 C60 70 80 55 92 12 C82 22 62 40 50 40 C38 40 18 22 8 12 Z' fill='#1f2937'/></svg>`
  );

const PX_PER_MM = 96 / 25.4;

function countLines(text, cpl) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  if (!words.length || cpl < 1) return 1;
  let lines = 1, cur = 0;
  for (const w of words) {
    const wl = w.length;
    if (cur === 0) cur = wl;
    else if (cur + 1 + wl <= cpl) cur += 1 + wl;
    else { lines++; cur = wl; }
    if (cur > cpl) { lines += Math.floor((cur - 1) / cpl); cur = ((cur - 1) % cpl) + 1; }
  }
  return lines;
}

function fitFontPt(name, textWmm, availHmm) {
  const MM2PT = 2.8346;
  const availW = textWmm * MM2PT;
  const availH = availHmm * MM2PT;
  for (let s = 13; s >= 4; s -= 0.5) {
    const cpl = Math.max(1, Math.floor(availW / (0.62 * s)));
    if (countLines(name, cpl) * s * 1.32 <= availH) return s;
  }
  return 4;
}

// Cari satu ukuran font seragam yang muat untuk SEMUA baris teks (nama produk + varian).
function fitUniformFontPt(texts, textWmm, availHmm, maxPt = 11) {
  const MM2PT = 2.8346;
  const availW = textWmm * MM2PT;
  const availH = availHmm * MM2PT;
  const list = texts.filter(Boolean);
  if (!list.length) return 4;
  for (let s = maxPt; s >= 4; s -= 0.5) {
    const cpl = Math.max(1, Math.floor(availW / (0.60 * s)));
    const total = list.reduce((sum, t) => sum + countLines(t, cpl), 0);
    if (total * s * 1.28 <= availH) return s;
  }
  return 4;
}


const PAPER_SIZES = {
  "50x20": { label: "50 × 20 mm", w: 50, h: 20 },
  "50x30": { label: "50 × 30 mm", w: 50, h: 30 },
  "40x30": { label: "40 × 30 mm", w: 40, h: 30 },
  "40x25": { label: "40 × 25 mm", w: 40, h: 25 },
  "33x15": { label: "33 × 15 mm", w: 33, h: 15 },
  "58": { label: "Thermal 58 mm", w: 58, h: 40 },
  "a4": { label: "Kertas Biasa (A4)", w: null, h: null },
};

export default function ProductLabels() {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [qrUrl, setQrUrl] = useState("");
  const [cfg, setCfg] = useState({ logo: "", paper: "50x30" });
  const [paper, setPaper] = useState("50x30");
  const labelRef = useRef(null);

  useEffect(() => {
    api.get("/settings/label").then((r) => { setCfg(r.data); setPaper(r.data.paper || "50x30"); }).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(q); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/products/labels", { params: { q: debounced || undefined, page, limit } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Gagal memuat label"))
      .finally(() => setLoading(false));
  }, [debounced, page, limit]);

  const openLabel = async (row) => {
    setSelected(row);
    try {
      const url = await QRCode.toDataURL(row.qr_value || row.name || row.code, { margin: 0, width: 480, errorCorrectionLevel: "M" });
      setQrUrl(url);
    } catch { setQrUrl(""); }
  };

  const logoSrc = cfg.logo || VLOGO;

  const labelHtml = (row, qr, sizeKey) => {
    const p = PAPER_SIZES[sizeKey] || PAPER_SIZES.a4;
    const pname = (row.product_name || row.name || "").trim();
    const cs = [row.color, row.size].filter(Boolean).join(" ").trim();
    if (sizeKey === "50x20") {
      // Layout presisi 50×20: font nama produk & varian dibuat SERAGAM, logo sedikit lebih besar.
      const pad = 1.0;
      const gap = 1.4;
      const qrSize = +(p.h - pad * 2).toFixed(2);
      const textW = +(p.w - pad * 2 - qrSize - gap).toFixed(2);
      const logoH = 5.6;
      const vgap = 0.5;
      const availTextH = +(p.h - pad * 2 - logoH - vgap * 2).toFixed(2);
      const fs = fitUniformFontPt([pname, cs], textW, availTextH, 10.5);
      return `
      <div style="width:${p.w}mm;height:${p.h}mm;box-sizing:border-box;display:flex;align-items:center;gap:${gap}mm;padding:${pad}mm;font-family:Arial,Helvetica,sans-serif;overflow:hidden;background:#fff;">
        <img src="${qr}" style="width:${qrSize}mm;height:${qrSize}mm;flex:none;" />
        <div style="width:${textW}mm;flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${vgap}mm;text-align:center;overflow:hidden;">
          <img src="${logoSrc}" style="height:${logoH}mm;max-width:92%;object-fit:contain;" />
          <div style="font-weight:800;font-size:${fs}pt;line-height:1.15;color:#000;text-transform:uppercase;word-break:break-word;letter-spacing:0.2px;">${pname}</div>
          ${cs ? `<div style="font-weight:600;font-size:${fs}pt;line-height:1.15;color:#000;text-transform:uppercase;letter-spacing:0.3px;">${cs}</div>` : ""}
        </div>
      </div>`;
    }
    if (p.w && p.h) {
      const pad = 0.8;
      const gap = 1.2;
      const qrSize = +(p.h - pad * 2).toFixed(2);
      const textW = +(p.w - pad * 2 - qrSize - gap).toFixed(2);
      const logoH = Math.max(2.2, p.h * 0.13);
      const csH = cs ? Math.max(2.0, p.h * 0.13) : 0;
      const availNameH = +(p.h - pad * 2 - logoH - 0.6 - (csH ? csH + 0.4 : 0)).toFixed(2);
      const nameFs = fitFontPt(pname, textW, availNameH);
      const csFs = cs ? Math.min(nameFs * 0.82, fitFontPt(cs, textW, csH)) : 0;
      return `
      <div style="width:${p.w}mm;height:${p.h}mm;box-sizing:border-box;display:flex;align-items:center;gap:${gap}mm;padding:${pad}mm;font-family:Arial,Helvetica,sans-serif;overflow:hidden;background:#fff;">
        <img src="${qr}" style="width:${qrSize}mm;height:${qrSize}mm;flex:none;" />
        <div style="width:${textW}mm;flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:0.4mm;text-align:center;overflow:hidden;">
          <img src="${logoSrc}" style="height:${logoH}mm;max-width:70%;object-fit:contain;" />
          <div style="font-weight:800;font-size:${nameFs}pt;line-height:1.12;color:#000;text-transform:uppercase;word-break:break-word;">${pname}</div>
          ${cs ? `<div style="font-weight:600;font-size:${csFs}pt;line-height:1.1;color:#000;text-transform:uppercase;letter-spacing:0.2px;">${cs}</div>` : ""}
        </div>
      </div>`;
    }
    return `
      <div style="display:flex;align-items:center;gap:16px;padding:8px 12px;font-family:Arial,Helvetica,sans-serif;">
        <img src="${qr}" style="width:150px;height:150px;" />
        <div style="display:flex;flex-direction:column;align-items:center;gap:5px;max-width:240px;text-align:center;">
          <img src="${logoSrc}" style="width:40px;height:32px;object-fit:contain;" />
          <div style="font-weight:800;font-size:17px;line-height:1.12;color:#111;text-transform:uppercase;">${pname}</div>
          ${cs ? `<div style="font-weight:600;font-size:12px;color:#111;text-transform:uppercase;">${cs}</div>` : ""}
        </div>
      </div>`;
  };

  const doPrint = () => {
    if (!selected || !qrUrl) return;
    const p = PAPER_SIZES[paper] || PAPER_SIZES.a4;
    const pageCss = p.w && p.h ? `@page{size:${p.w}mm ${p.h}mm;margin:0;}` : `@page{margin:6mm;}`;
    const w = window.open("", "_blank", "width=640,height=420");
    if (!w) return toast.error("Popup diblokir browser");
    w.document.write(`<!doctype html><html><head><title>Product Label</title>
      <style>${pageCss} html,body{margin:0;padding:0;} body{display:flex;justify-content:center;align-items:flex-start;}</style>
      </head><body>${labelHtml(selected, qrUrl, paper)}
      <script>window.onload=function(){setTimeout(function(){window.print();},250);};<\/script>
      </body></html>`);
    w.document.close();
  };

  const doDownload = async () => {
    if (!labelRef.current) return;
    try {
      const canvas = await html2canvas(labelRef.current, { backgroundColor: "#fff", scale: 3 });
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `label-${(selected?.code || "product")}.png`;
      a.click();
    } catch { toast.error("Gagal membuat PNG"); }
  };

  const previewP = PAPER_SIZES[paper] || PAPER_SIZES.a4;
  const previewScale = previewP.w ? Math.min(7, 380 / (previewP.w * PX_PER_MM)) : 1.5;
  const previewW = previewP.w ? previewP.w * PX_PER_MM * previewScale : 380;
  const previewH = previewP.h ? previewP.h * PX_PER_MM * previewScale : 210;

  const start = (data.page - 1) * limit;
  const pageNumbers = useMemo(() => {
    const pages = data.pages || 1;
    const cur = data.page || 1;
    const win = [];
    const push = (n) => { if (!win.includes(n) && n >= 1 && n <= pages) win.push(n); };
    push(1); push(2);
    for (let i = cur - 1; i <= cur + 1; i++) push(i);
    push(pages - 1); push(pages);
    const sorted = win.sort((a, b) => a - b);
    const out = [];
    let prev = 0;
    for (const n of sorted) { if (n - prev > 1) out.push("..."); out.push(n); prev = n; }
    return out;
  }, [data.pages, data.page]);

  return (
    <div data-testid="product-labels-page">
      <PageHeader title="Product Label" subtitle="Ventedaily / Product Label" />
      <Panel className="p-0">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-taupe/15">
          <h2 className="text-base font-semibold text-cocoa">Product Labels</h2>
          <div className="relative w-64 max-w-full">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-wmuted" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari produk / kode..."
              className="pl-8 h-9"
              data-testid="label-search"
            />
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">No</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-24 text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={3} className="text-center py-10 text-wmuted">Memuat...</TableCell></TableRow>
            ) : data.items.length === 0 ? (
              <TableRow><TableCell colSpan={3} className="text-center py-10 text-wmuted">Tidak ada data</TableCell></TableRow>
            ) : (
              data.items.map((row, i) => (
                <TableRow key={row.variant_id} data-testid={`label-row-${row.variant_id}`}>
                  <TableCell className="text-wmuted">{start + i + 1}</TableCell>
                  <TableCell className="font-medium text-cocoa">{row.name}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm" variant="ghost"
                      className="text-cocoa hover:bg-taupe/10"
                      onClick={() => openLabel(row)}
                      data-testid={`label-print-${row.variant_id}`}
                    >
                      <QrCode className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-taupe/15 text-sm">
          <div className="flex items-center gap-2 text-wmuted">
            <span>Show</span>
            <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
              <SelectTrigger className="h-8 w-20" data-testid="label-limit"><SelectValue /></SelectTrigger>
              <SelectContent>{[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
            </Select>
            <span>entries · Menampilkan {data.total === 0 ? 0 : start + 1}–{Math.min(start + limit, data.total)} dari {data.total}</span>
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" disabled={data.page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="label-prev">Previous</Button>
            {pageNumbers.map((n, idx) =>
              n === "..." ? (
                <span key={`e${idx}`} className="px-2 text-wmuted">…</span>
              ) : (
                <Button key={n} size="sm" variant={n === data.page ? "default" : "outline"} onClick={() => setPage(n)}>{n}</Button>
              )
            )}
            <Button size="sm" variant="outline" disabled={data.page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="label-next">Next</Button>
          </div>
        </div>
      </Panel>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setQrUrl(""); } }}>
        <DialogContent className="max-w-xl" data-testid="label-dialog">
          <DialogHeader>
            <DialogTitle>Print Product Label</DialogTitle>
            <DialogDescription>Cetak atau unduh label QR untuk produk terpilih.</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <p className="text-center text-sm text-wmuted">
                <span className="font-semibold text-cocoa">Product:</span> {selected.name}
              </p>
              <div className="flex justify-center py-2">
                <div
                  style={{ width: previewW, height: previewH }}
                  className="relative overflow-hidden rounded-md border border-taupe/30 bg-white shadow-sm"
                  data-testid="label-preview-wrap"
                >
                  {qrUrl ? (
                    <div style={{ transform: `scale(${previewScale})`, transformOrigin: "top left" }}>
                      <div ref={labelRef} data-testid="label-preview" dangerouslySetInnerHTML={{ __html: labelHtml(selected, qrUrl, paper) }} />
                    </div>
                  ) : <div className="h-full w-full bg-slate-100" />}
                </div>
              </div>
              <p className="text-center text-xs text-wmuted">Pratinjau skala nyata ({previewP.label})</p>
              <div className="flex items-center justify-center gap-2">
                <span className="text-sm text-wmuted">Ukuran Kertas:</span>
                <Select value={paper} onValueChange={setPaper}>
                  <SelectTrigger className="h-9 w-52" data-testid="label-paper"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PAPER_SIZES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-center gap-3">
                <Button onClick={doPrint} disabled={!qrUrl} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="label-do-print">
                  <Printer className="w-4 h-4 mr-2" />Print Label
                </Button>
                <Button onClick={doDownload} disabled={!qrUrl} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="label-do-download">
                  <Download className="w-4 h-4 mr-2" />Download PNG
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSelected(null); setQrUrl(""); }} data-testid="label-cancel">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
